'use strict'
const { WATCHDOG_CONDITIONS, SUPERADMIN_EVENTS, eventVisible } = require('./operationalAlertPolicy')
// The one alert catalog: what Settings shows on the web and in the app (alertPreferenceModel.js), and what alert delivery
// sends for anything a person has not saved (utils/alertDelivery.js keeps no defaults of its own since 2026-11-03;
// docs/bugs/alert_defaults_two_lists_BUG.md). SMS subscriptions are explicitly off by default.
/**
 * Default channel settings (master switches)
 * Hub is now included as a controllable channel
 */
const DEFAULT_CHANNELS = {
  badge: true,
  dropdown: true,
  hub: true,
  overlay: true,
  email: true,
  sms: false,
  push: false,
  sound: false,
}

/**
 * Default consolidation settings per event type
 * consolidateAfter: number of individual alerts before consolidating
 * 0 = never consolidate, always show individual items
 */
const DEFAULT_CONSOLIDATION = {
  unreadText: { enabled: true, after: 3 },
  unreadEmail: { enabled: true, after: 3 },
  unreadChat: { enabled: true, after: 3 },
  noteTagged: { enabled: true, after: 3 },
  noteAll: { enabled: true, after: 5 },
  voicemail: { enabled: true, after: 3 },
  unknownVoicemail: { enabled: true, after: 3 },
  unknownText: { enabled: true, after: 3 },
  unknownEmail: { enabled: true, after: 3 },
  customerCompleted: { enabled: false, after: 5 },
  customerCanceled: { enabled: false, after: 3 },
  customerReactivated: { enabled: false, after: 5 },
  customerOnHold: { enabled: false, after: 5 },
  customerReleasedFromHold: { enabled: false, after: 5 },
  taskRejected: { enabled: false, after: 3 },
  leadPool: { enabled: false, after: 0 },
  incomingCall: { enabled: false, after: 0 },
  missedCall: { enabled: true, after: 3 },
  missedCallBrowser: { enabled: true, after: 3 },
  calendarEventAccepted: { enabled: true, after: 3 },
  calendarEventDeclined: { enabled: false, after: 3 },
  calendarEventTentative: { enabled: true, after: 3 },
  communicationResolutionNeeded: { enabled: true, after: 3 },
  powerHubAlertAutoCreate: { enabled: true, after: 3 },
  workflowAlert: { enabled: true, after: 5 },
  inventoryLowStock: { enabled: true, after: 5 },
  inventoryCritical: { enabled: true, after: 3 },
  kittingProblem: { enabled: true, after: 3 },
  expenseSubmitted: { enabled: false, after: 3 },
  expenseApproved: { enabled: false, after: 3 },
  expenseRejected: { enabled: false, after: 3 },
  mretsOverrideStale: { enabled: true, after: 5 },
  mretsOverrideResponseReceived: { enabled: false, after: 3 },
  mretsAutomation: { enabled: false, after: 3 },
  // AI Reports (Phase 77) — fleet's executive briefings
  aiReportNew: { enabled: true, after: 5 }, // routine reports are noisy; consolidate aggressively
  aiReportActionable: { enabled: true, after: 3 }, // actionable reports are higher-signal; consolidate less
  aiReportChatReply: { enabled: false, after: 3 }, // 1:1 reply on a report thread — individual is fine
  aiReportStuck: { enabled: false, after: 3 }, // operator already knows what's stuck — individual ok
  aiReportWeeklyDigest: { enabled: false, after: 0 }, // weekly = one-at-a-time, never consolidate
  financePlanChanged: { enabled: false, after: 0 }, // nightly, at most one a day — never consolidate
  financePlanSourceProblem: { enabled: false, after: 0 }, // nightly, at most one a day — never consolidate
}

/**
 * Default per-event settings
 * Each event has settings for which channels should fire
 * Hub is now configurable (though typically locked by admin)
 */
const DEFAULT_EVENTS = {
  buildPartnerJobArrived: {
    badge: false,
    dropdown: false,
    hub: false,
    overlay: false,
    email: false,
    sms: false,
    push: false,
    sound: false,
  },
  // Communications
  unreadText: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  unreadEmail: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  unreadChat: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: true,
  },
  noteTagged: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: false,
    sound: false,
  },
  noteAll: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  voicemail: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: false,
    sound: true,
  },
  unknownVoicemail: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: false,
    sound: true,
  },
  // Text / email from a sender we cannot match (an Unknown-tab record; receiveTextReply / fetchEmailReplies). Until
  // 2026-10-10 neither kind was registered, so the alert was dropped: a prospect's first text sat unseen for 13 days
  // (docs/bugs/unknown_inbound_alerts_never_delivered_BUG.md). A text copies unknownVoicemail; an email is quieter (no
  // popup, no sound), like unreadEmail, because unknown mail includes vendors.
  unknownText: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: false,
    sound: true,
  },
  unknownEmail: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },

  // Lifecycle
  customerCompleted: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
  customerCanceled: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: true,
  },
  customerReactivated: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  customerOnHold: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: false,
    sound: false,
  },
  customerReleasedFromHold: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },

  // Tasks
  taskRejected: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: false,
    sound: false,
  },

  // Lead Pool
  leadPool: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },

  // Calls (some channels N/A, marked with null)
  incomingCall: {
    badge: null,
    dropdown: null,
    hub: true,
    overlay: true,
    email: null,
    push: null,
    sound: true,
  },
  missedCall: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  missedCallBrowser: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },

  // Calendar Events
  calendarEventAccepted: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: true,
  },
  calendarEventDeclined: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: true,
  },
  calendarEventTentative: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },

  // Resolution Tracking
  communicationResolutionNeeded: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: true,
    sound: true,
  },

  // Power Hub Auto-Create
  powerHubAlertAutoCreate: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: true,
  },

  // Workflow/Notification Library
  workflowAlert: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: true,
    sound: false,
  },

  // Inventory
  inventoryLowStock: {
    badge: false,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  inventoryCritical: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: false,
    sound: false,
  },

  // Kitting
  kittingProblem: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: false,
    sound: false,
  },

  // Expense Reports & Procurement (Phase 57)
  expenseSubmitted: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
  expenseApproved: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
  expenseRejected: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: false,
    sound: false,
  },

  // M-RETs Override Tracking
  mretsOverrideStale: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
  mretsOverrideResponseReceived: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: true,
  },
  mretsAutomation: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: false,
  },

  // AI Reports (Phase 77) — tiered: routine = badge + dropdown only; actionable = full alert.
  aiReportNew: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  aiReportActionable: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: false,
    push: true,
    sound: true,
  },
  aiReportChatReply: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  aiReportStuck: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
  aiReportWeeklyDigest: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  // Finance planner nightly check (docs/features/finance_planner_FEATURE.md M4): investment needed moved $15K+.
  financePlanChanged: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
  // Finance planner M7 (§9d): a source the plan reads needs a person (QuickBooks needs reconnecting).
  financePlanSourceProblem: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: false,
    sound: false,
  },
}

Object.assign(DEFAULT_EVENTS, {
  // Milestone slip detection (Phase 91): a twice-daily digest-class warning, so the bell surfaces only.
  milestoneSlip: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  // Emergency Communication (38C): high priority on every channel.
  emergencyCommunication: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: true,
    email: true,
    push: true,
    sound: true,
  },
  bugReportSubmitted: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  bugReportStatusChanged: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  // Requests to an AI teammate (Phase 69G author requests; "Ask Vesta" site analyses). The ephemeral alert must be
  // written (one of badge, dropdown, hub, overlay or sound on): the AI's watcher wakes her session on any new one.
  // Hub-quiet: the operator is already looking at the request.
  aiAuthorRequest: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  siteAnalysisRequest: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  // AI teammate as a thread's draft author (docs/features/aurora_inline_drafts_FEATURE.md). aiDraftRequest goes to the
  // AI (her watcher wakes her on the written ephemeral alert); aiDraftReady goes to the people who review her drafts,
  // with push, so a tap opens the lead and the draft.
  aiDraftRequest: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  aiDraftReady: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: true,
    sound: true,
  },
  // Aurora asks to switch a paid campaign on, resume it or raise its budget
  // (docs/features/paid_campaign_launch_FEATURE.md P3). Sent to paidCampaignConfig.approverUids; the request also waits
  // on the campaign page, which the alert opens.
  paidCampaignApproval: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: true,
    push: true,
    sound: false,
  },
  // The blog's approval flow (functions-growth/marketing/blogPosts.js; docs/bugs/blog_alerts_never_delivered_BUG.md). A
  // draft to approve goes to the content plan's approvers on the bell and push, never a text; the decision goes back to
  // the AI that wrote it, on the bell, which is what wakes her.
  blogDraftSubmitted: {
    badge: true,
    dropdown: true,
    hub: true,
    overlay: false,
    email: false,
    push: true,
    sound: false,
  },
  blogDraftPublished: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  blogDraftReturned: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  // A social post an AI teammate drafted, sent back from review (saveSocialPost; docs/bugs/
  // social_post_returned_silently_BUG.md): the blog's returned draft, on the same bell that wakes her.
  socialPostReturned: {
    badge: true,
    dropdown: true,
    hub: false,
    overlay: false,
    email: false,
    push: false,
    sound: false,
  },
  // These notices have separate, established in-app producers; the row adds only a personal SMS subscription
  // (CHANNEL_NOT_APPLICABLE below). systemMonitoring's row is the quiet bell row, set further down.
  systemMonitoring: {},
  nightlyGuardFailure: {},
  expenseAccountantNotice: {},
  maintenanceTaskDue: {},
  holdEscalated: {},
})
for (const settings of Object.values(DEFAULT_EVENTS)) settings.sms = false

/**
 * Event metadata for UI display
 */
const EVENT_METADATA = {
  buildPartnerJobArrived: {
    label: 'New build partnership job',
    category: 'Build Partnerships',
    icon: 'Wrench',
  },
  // Communications
  unreadText: { label: 'Unread text message', category: 'Communications', icon: 'MessageSquare' },
  unreadEmail: { label: 'Unread email', category: 'Communications', icon: 'Mail' },
  unreadChat: { label: 'Unread chat', category: 'Communications', icon: 'MessageCircle' },
  noteTagged: { label: 'Tagged in note', category: 'Communications', icon: 'AtSign' },
  noteAll: { label: 'New note on my customers', category: 'Communications', icon: 'StickyNote' },
  voicemail: { label: 'Voicemail received', category: 'Communications', icon: 'Voicemail' },
  unknownVoicemail: {
    label: 'Voicemail (unknown caller)',
    category: 'Communications',
    icon: 'Voicemail',
  },
  unknownText: {
    label: 'Text from an unknown number',
    category: 'Communications',
    icon: 'MessageSquare',
  },
  unknownEmail: {
    label: 'Email from an unknown sender',
    category: 'Communications',
    icon: 'Mail',
  },

  // Lifecycle
  customerCompleted: {
    label: 'Customer completed',
    category: 'Customer Lifecycle',
    icon: 'CheckCircle2',
  },
  customerCanceled: { label: 'Customer canceled', category: 'Customer Lifecycle', icon: 'XCircle' },
  customerReactivated: {
    label: 'Customer reactivated',
    category: 'Customer Lifecycle',
    icon: 'RefreshCw',
  },
  customerOnHold: {
    label: 'Customer on hold',
    category: 'Customer Lifecycle',
    icon: 'PauseCircle',
  },
  customerReleasedFromHold: {
    label: 'Customer released from hold',
    category: 'Customer Lifecycle',
    icon: 'PlayCircle',
  },

  // Tasks
  taskRejected: { label: 'Task rejected', category: 'Tasks', icon: 'AlertTriangle' },

  // Lead Pool
  leadPool: { label: 'New leads available', category: 'Lead Pool', icon: 'Target' },

  // Calls
  incomingCall: { label: 'Incoming call', category: 'Calls', icon: 'PhoneIncoming' },
  missedCall: { label: 'Missed call', category: 'Calls', icon: 'PhoneMissed' },
  missedCallBrowser: { label: 'Missed call (browser)', category: 'Calls', icon: 'PhoneMissed' },

  // Calendar Events
  calendarEventAccepted: {
    label: 'Worker accepted calendar event',
    category: 'Calendar',
    icon: 'CalendarCheck',
  },
  calendarEventDeclined: {
    label: 'Worker declined calendar event',
    category: 'Calendar',
    icon: 'CalendarX',
  },
  calendarEventTentative: {
    label: 'Worker tentatively accepted',
    category: 'Calendar',
    icon: 'CalendarClock',
  },

  // Resolution Tracking
  communicationResolutionNeeded: {
    label: 'Communication needs response',
    category: 'Communications',
    icon: 'MessageSquare',
  },

  // Power Hub Auto-Create
  powerHubAlertAutoCreate: {
    label: 'Service Case Auto-Created',
    category: 'Automation',
    icon: 'Wrench',
  },

  // Workflow/Notification Library
  workflowAlert: { label: 'Workflow notifications', category: 'Automation', icon: 'Bell' },

  // Inventory
  inventoryLowStock: { label: 'Low stock warning', category: 'Inventory', icon: 'Package' },
  inventoryCritical: { label: 'Critical stock alert', category: 'Inventory', icon: 'Package' },

  // Kitting
  kittingProblem: { label: 'Kitting problem', category: 'Kitting', icon: 'PackageX' },

  // Expense Reports & Procurement (Phase 57)
  expenseSubmitted: {
    label: 'Expense report submitted (waiting on me)',
    category: 'Expenses',
    icon: 'Receipt',
  },
  expenseApproved: {
    label: 'My expense report was approved',
    category: 'Expenses',
    icon: 'CheckCircle2',
  },
  expenseRejected: {
    label: 'My expense report was rejected',
    category: 'Expenses',
    icon: 'XCircle',
  },

  // M-RETs Override Tracking
  mretsOverrideStale: {
    label: 'M-RETs override sitting unresolved',
    category: 'Automation',
    icon: 'AlertTriangle',
  },
  mretsOverrideResponseReceived: {
    label: 'M-RETs replied to override request',
    category: 'Automation',
    icon: 'MailCheck',
  },
  mretsAutomation: {
    label: 'M-RETs rejection or automation needs attention',
    category: 'Automation',
    icon: 'AlertTriangle',
  },

  // AI Reports (Phase 77) — fleet's executive briefings
  aiReportNew: { label: 'New AI report', category: 'AI Reports', icon: 'FileText' },
  aiReportActionable: {
    label: 'AI report needs your input',
    category: 'AI Reports',
    icon: 'AlertCircle',
  },
  aiReportChatReply: {
    label: 'AI replied to your question',
    category: 'AI Reports',
    icon: 'MessageCircleReply',
  },
  aiReportStuck: { label: 'AI waiting on you > 24h', category: 'AI Reports', icon: 'Clock' },
  aiReportWeeklyDigest: {
    label: 'Weekly AI digest available',
    category: 'AI Reports',
    icon: 'CalendarRange',
  },

  // Finance planner (planner roles only receive it)
  financePlanChanged: {
    label: 'Finance plan: investment needed moved $15K or more',
    category: 'Finance',
    icon: 'AlertTriangle',
  },
  financePlanSourceProblem: {
    label: 'Finance plan: QuickBooks needs to be reconnected',
    category: 'Finance',
    icon: 'AlertTriangle',
  },
}

// Extend existing categories without reordering the established Settings sections.
Object.assign(EVENT_METADATA, {
  milestoneSlip: {
    label: 'Milestone needs rescheduling',
    category: 'Tasks',
    icon: 'CalendarClock',
  },
  maintenanceTaskDue: { label: 'Maintenance task due', category: 'Tasks', icon: 'Wrench' },
  holdEscalated: {
    label: 'Customer hold needs attention',
    category: 'Tasks',
    icon: 'AlertTriangle',
  },
  emergencyCommunication: {
    label: 'Emergency communication',
    category: 'Communications',
    icon: 'AlertTriangle',
  },
  bugReportSubmitted: {
    label: 'Bug or feature request submitted',
    category: 'System',
    icon: 'Bell',
  },
  bugReportStatusChanged: {
    label: 'Bug or feature request status changed',
    category: 'System',
    icon: 'Bell',
  },
  systemMonitoring: {
    label: 'System Monitoring Alerts',
    category: 'System',
    icon: 'Shield',
  },
  nightlyGuardFailure: {
    label: 'Nightly guard needs attention',
    category: 'System',
    icon: 'Shield',
  },
  expenseAccountantNotice: {
    label: 'Approved report ready for accounting',
    category: 'Expenses',
    icon: 'Mail',
  },
  aiAuthorRequest: {
    label: 'AI author request or follow-up',
    category: 'Automation',
    icon: 'MessageSquare',
  },
  siteAnalysisRequest: {
    label: 'Site analysis request or follow-up',
    category: 'Automation',
    icon: 'MessageSquare',
  },
  aiDraftRequest: {
    label: 'Reply draft requested (AI teammate)',
    category: 'Automation',
    icon: 'MessageSquare',
  },
  aiDraftReady: {
    label: 'AI teammate drafted a reply to review',
    category: 'Communications',
    icon: 'MessageSquare',
  },
  paidCampaignApproval: {
    label: 'Aurora asks to switch a paid campaign on or spend more',
    category: 'Automation',
    icon: 'Bell',
  },
  blogDraftSubmitted: {
    label: 'Blog post waiting for your approval',
    category: 'Automation',
    icon: 'FileText',
  },
  blogDraftPublished: {
    label: 'Your blog post was published (AI teammate)',
    category: 'Automation',
    icon: 'FileText',
  },
  blogDraftReturned: {
    label: 'Your blog post was sent back (AI teammate)',
    category: 'Automation',
    icon: 'FileText',
  },
  socialPostReturned: {
    label: 'Your social post was sent back (AI teammate)',
    category: 'Automation',
    icon: 'MessageCircle',
  },
})

const quietAlert = { badge: true, dropdown: true, hub: true, overlay: false, email: false, sms: false, push: false, sound: false }
// Newly added health rows keep the existing bell behavior. Every outbound
// channel, especially SMS, needs an explicit personal choice.
for (const [kind, label] of Object.values(WATCHDOG_CONDITIONS)) {
  EVENT_METADATA[kind] = { label, category: 'Agent Watchdog', icon: 'Shield', superAdminOnly: true }
  DEFAULT_EVENTS[kind] = { ...quietAlert }
}
EVENT_METADATA.phoneSystemHealth = { label: 'Phone System Health', category: 'System', icon: 'Phone', superAdminOnly: true }
EVENT_METADATA.systemMonitoring.superAdminOnly = true
DEFAULT_EVENTS.phoneSystemHealth = { ...quietAlert }
DEFAULT_EVENTS.systemMonitoring = { ...quietAlert }
EVENT_METADATA.mretsRunSummary = { label: 'M-RETs run summary', category: 'Automation', icon: 'FileText' }
DEFAULT_EVENTS.mretsRunSummary = { ...quietAlert }
DEFAULT_EVENTS.mretsAutomation = { ...quietAlert }
// Site Monitoring's quiet switches in how sites are judged (Erik 2026-10-18): the meter by its own counter
// (docs/features/rgm_meter_tracking_FEATURE.md §3.4) and expected production aged with the panels
// (docs/features/production_degradation_FEATURE.md §5). One summary per switch, to the M-RETs automation alert recipients; in-app
// and email by default. (The id predates the second switch.)
EVENT_METADATA.siteMonitoringMeterSwitch = { label: 'Site Monitoring: how sites are judged changed', category: 'Automation', icon: 'FileText' }
DEFAULT_EVENTS.siteMonitoringMeterSwitch = { ...quietAlert, email: true }
// The standing M-RETs ledger check (docs/bugs/mrets_filing_history_diverges_from_mrets_BUG.md): M-RETs and the CRM's filing
// history disagree. One alert per new set of disagreements, to the M-RETs automation alert recipients; in-app and email.
EVENT_METADATA.mretsLedgerDivergence = { label: 'M-RETs and the CRM filing history disagree', category: 'Automation', icon: 'FileText' }
DEFAULT_EVENTS.mretsLedgerDivergence = { ...quietAlert, email: true }

/**
 * Channels that are not applicable for certain events
 */
const CHANNEL_NOT_APPLICABLE = {
  incomingCall: ['badge', 'dropdown', 'email', 'push'],
  // Their established notices are managed by the producing module. This row
  // adds a personal SMS subscription without pretending to control those notices.
  ...Object.fromEntries(
    [
      'nightlyGuardFailure',
      'expenseAccountantNotice',
      'maintenanceTaskDue',
      'holdEscalated',
    ].map(kind => [kind, ['badge', 'dropdown', 'hub', 'overlay', 'email', 'push', 'sound']])
  ),
}

/**
 * Current schema version for migration tracking
 */
const SCHEMA_VERSION = 3 // Bumped for consolidation support

module.exports = {
  DEFAULT_CHANNELS,
  DEFAULT_CONSOLIDATION,
  DEFAULT_EVENTS,
  EVENT_METADATA,
  CHANNEL_NOT_APPLICABLE,
  SCHEMA_VERSION,
  SUPERADMIN_EVENTS,
  eventVisible,
}
